import React, { useState, useEffect } from 'react';
import { X, CreditCard, FileText, Mail, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { trackPaymentModalOpen, trackReportTypeSelected, trackRedirectToStripe, trackError } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASIC_PAYMENT_LINK = process.env.REACT_APP_BASIC_PAYMENT_LINK;
const PREMIUM_PAYMENT_LINK = process.env.REACT_APP_PREMIUM_PAYMENT_LINK;

const PaymentModal = ({ isOpen, onClose, contractId, fileName }) => {
  const [email, setEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Track modal open
  useEffect(() => {
    if (isOpen && contractId) {
      trackPaymentModalOpen(contractId);
    }
  }, [isOpen, contractId]);

  if (!isOpen) return null;

  const plans = [
    {
      id: 'basic',
      name: 'Report Basic',
      price: '2',
      originalPrice: '5',
      features: [
        'Sommario del contratto',
        'Clausole critiche',
        'Valutazione rischi',
        'Azioni consigliate'
      ],
      paymentLink: BASIC_PAYMENT_LINK
    },
    {
      id: 'premium',
      name: 'Report Premium',
      price: '5',
      originalPrice: '19',
      popular: true,
      features: [
        'Tutto del Basic',
        'Analisi dettagliata',
        'Confronto mercato ARERA',
        'Consigli strategici',
        'Risparmi stimati'
      ],
      paymentLink: PREMIUM_PAYMENT_LINK
    }
  ];

  const handleProceedToPayment = async () => {
    // Validate email
    if (!email || !email.includes('@')) {
      setError('Inserisci un indirizzo email valido');
      trackError('validation_error', 'Invalid email format');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Track report type selection
      const selectedPlanData = plans.find(p => p.id === selectedPlan);
      trackReportTypeSelected(selectedPlan, parseFloat(selectedPlanData.price));

      // 1. Save email and report_type to backend BEFORE redirect
      await axios.post(`${BACKEND_URL}/api/contract/update-payment-info`, {
        contract_id: contractId,
        email: email,
        report_type: selectedPlan
      });
      
      // 2. Build payment URL with contract_id in BOTH client_reference_id AND metadata
      const paymentUrl = new URL(selectedPlanData.paymentLink);
      
      // Aggiungi come URL parameter (diventa client_reference_id)
      paymentUrl.searchParams.append('client_reference_id', contractId);
      
      // Aggiungi anche come metadata (backup per Stripe Payment Links)
      paymentUrl.searchParams.append('metadata[contract_id]', contractId);
      paymentUrl.searchParams.append('metadata[report_type]', selectedPlan);
      
      // Prefill email
      paymentUrl.searchParams.append('prefilled_email', email);
      
      // 3. Store in localStorage for success page
      localStorage.setItem('payment_pending', JSON.stringify({
        contractId,
        email,
        reportType: selectedPlan,
        fileName
      }));

      // Track redirect to Stripe
      trackRedirectToStripe(selectedPlan, email);

      // 4. Redirect to Stripe payment
      window.location.href = paymentUrl.toString();
      
    } catch (err) {
      console.error('Payment setup error:', err);
      const errorMsg = 'Errore nel salvataggio dei dati. Riprova.';
      setError(errorMsg);
      setLoading(false);
      trackError('payment_setup_failed', err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-green-500 text-white p-6 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold mb-1">Scegli il tuo report</h2>
            <p className="text-sm opacity-90">Contratto: {fileName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Email Input */}
          <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
              <Mail className="w-4 h-4 mr-2 text-blue-600" />
              Email (per ricevere il report)
            </label>
            <input
              type="email"
              placeholder="tua@email.it"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg"
              required
            />
            {error && (
              <p className="text-red-600 text-sm mt-2 font-medium">⚠️ {error}</p>
            )}
          </div>

          {/* Plan Selection */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative border-4 rounded-xl p-6 cursor-pointer transition-all hover:scale-105 ${
                  selectedPlan === plan.id
                    ? 'border-green-500 bg-gradient-to-br from-green-50 to-blue-50 shadow-xl'
                    : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-green-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                      ⭐ Più scelto
                    </span>
                  </div>
                )}

                {selectedPlan === plan.id && (
                  <div className="absolute -top-3 -right-3">
                    <div className="bg-green-500 rounded-full p-2 shadow-lg">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                  </div>
                )}

                <h3 className="text-xl font-bold text-gray-900 mb-3">{plan.name}</h3>
                
                <div className="mb-4">
                  <span className="text-gray-400 line-through text-lg mr-2">€{plan.originalPrice}</span>
                  <span className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                    €{plan.price}
                  </span>
                  <span className="ml-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                    -60%
                  </span>
                </div>

                <ul className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Payment Button */}
          <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600">Totale da pagare</p>
                <p className="text-3xl font-black text-gray-900">
                  €{plans.find(p => p.id === selectedPlan)?.price}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Pagamento sicuro</p>
                <div className="flex items-center space-x-1 mt-1">
                  <img src="https://cdn.brandfolder.io/KGT2DTA4/at/8vbr8k4mr5xjwk4hxq4t9vs/Stripe_wordmark_-_blurple.svg" alt="Stripe" className="h-6" />
                </div>
              </div>
            </div>

            <Button
              onClick={handleProceedToPayment}
              disabled={loading || !email}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg py-6 shadow-xl disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Reindirizzamento...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Procedi al pagamento sicuro
                </>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500 mt-3">
              🔒 Pagamento sicuro elaborato da Stripe • I tuoi dati sono protetti
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
